/**
 * `AW2` · `AW3` — el borde de ESCRITURA, hablado contra `admin-web` por HTTP.
 *
 * La mitad de lectura vive en `proveedor-datos.ts` y se consume por el
 * `DataProvider` de Refine. Ésta no pasa por ahí, y el porqué está abajo.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ① LO QUE ENTRA DEL REPO SIGUEN SIENDO SÓLO TIPOS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Misma regla que `proveedor-datos.ts`: `arquitectura.html` §6 dice
 * `web-dev ──✗──▶ cualquier modules/ (solo contracts/read-api + admin-api)`, y
 * acá el borde permitido es el segundo. Los imports son `import type`, así que
 * `esbuild` los borra antes de resolver nada; el alias `@contracts/*` existe
 * sólo en `tsconfig.json` y NO en `vite.config.ts`, de modo que importar un
 * VALOR de un contrato —por ejemplo `ADMIN_API_ENTRY`, que es literalmente la
 * ruta a `modules/admin-api/src/index.ts`— hace fallar el build.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ② POR QUÉ ESTO NO ES EL `DataProvider` DE REFINE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `AW1` dejó `create` / `update` / `deleteOne` tirando un `501`, esperando que
 * `contracts/admin-api` los cableara. Ese contrato ya existe y **igual no van
 * por ahí**, por tres razones que son del contrato y no de comodidad:
 *
 *   · `upload()` devuelve `IngestOutcome[]` —UN RESULTADO POR ARCHIVO— y
 *     `create` de Refine devuelve **un** registro. Meter el lote en `create`
 *     obliga a colapsar N resultados en uno, que es exactamente la
 *     implementación que el `guard` ③ de `AW2` prohíbe («el cartel único del
 *     lote»). La forma del contrato se perdería en la traducción.
 *   · `retry()` no es ninguna de las cuatro operaciones CRUD. Forzarlo a
 *     `update` inventaría un recurso y un campo que nadie escribió.
 *   · `deleteOne` de Refine trae actualización optimista de la caché. El
 *     `guard` ① de `AW3` prohíbe justamente eso: «no hay deshacer», y `D11`
 *     deja por escrito que no hay de dónde recuperar.
 *
 * Las cuatro operaciones de `DataProvider` siguen tirando `501`, ahora con el
 * motivo correcto: no es que falte el borde, es que este borde no cabe ahí.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ③ EL `actor` NO VIAJA DESDE ACÁ, Y ESO ES DEL CONTRATO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `contracts/admin-api/index.ts` lo dice con todas las letras: «**El actor NO lo
 * pone la pantalla.** Este puerto lo llama `apps/admin-web`, que es quien tiene
 * la sesión autenticada de Better Auth (ADR-0006). Un actor que viniera del
 * navegador sería un campo que el cliente elige, o sea una auditoría que el
 * auditado escribe».
 *
 * Consecuencia directa y no obvia: **`DocumentsAdminApi` NO es el borde de esta
 * pantalla.** Es el puerto que `admin-web` expone hacia adentro. Lo que la
 * pantalla puede emitir es el mismo mandato SIN `actor`. Por eso los tipos de
 * abajo se DERIVAN del contrato en vez de copiarlo: si mañana `UploadCommand`
 * gana un campo, aparece acá solo; si `DocumentsAdminApi` gana una operación,
 * `BordeDeEscritura` la gana y ninguna implementación compila hasta cablearla.
 */

import type {
  DocumentCommand,
  DocumentsAdminApi,
  IngestOutcome,
  UploadCommand,
  UploadConstraints,
} from "@contracts/admin-api/index.ts"
import type { ConfiguracionInstancia } from "./configuracion.ts"
import { mensajeDeError } from "./mensaje-de-error.ts"

export type { IngestOutcome, UploadConstraints }

/**
 * El lote tal como lo puede emitir un navegador.
 *
 * Dos diferencias con `UploadCommand`, y las dos son del lado de acá:
 *
 *   · **Sin `actor`** — ver ③ arriba.
 *   · **`File` del DOM en lugar de `UploadedFile`.** `UploadedFile` lleva los
 *     bytes ya materializados, que es la forma correcta del lado del servidor y
 *     la equivocada del lado del navegador: obligaría a leer cada archivo entero
 *     a memoria sólo para volcarlo a un `FormData` que ya sabe leerlo solo. Con
 *     40 MB por archivo y selección múltiple, eso es la pestaña quedándose sin
 *     memoria antes de mandar el primer byte.
 *
 * `topic` NO se redeclara: sale de `UploadCommand` por `Omit`, así que la
 * decisión «un tema por LOTE y no por archivo» —que el contrato tomó citando
 * «si se necesitan temas distintos, se cargan en lotes separados»— no se puede
 * desarmar desde acá sin que el tipo se queje.
 */
export type MandatoDeCarga = Omit<UploadCommand, "actor" | "files"> & {
  readonly files: readonly File[]
}

/** `DocumentCommand` sin el actor: qué documento, y nada más. */
export type MandatoDeDocumento = Omit<DocumentCommand, "actor">

/**
 * Cada operación del contrato, traducida a lo que la pantalla puede emitir.
 *
 * El `default` de la condicional es `DocumentsAdminApi[K]` **tal cual**: una
 * operación que el contrato agregue mañana entra acá con su firma original y
 * rompe a todo el que implemente `BordeDeEscritura` hasta que alguien la piense.
 * Es lo contrario de una interfaz escrita a mano, que se quedaría vieja en
 * silencio.
 */
type Traducida<K extends keyof DocumentsAdminApi> = K extends "upload"
  ? (mandato: MandatoDeCarga) => Promise<readonly IngestOutcome[]>
  : K extends "retry" | "remove"
    ? (mandato: MandatoDeDocumento) => Promise<void>
    : DocumentsAdminApi[K]

/**
 * El borde de escritura visto desde el navegador.
 *
 * Es `DocumentsAdminApi` con las tres operaciones que llevan `actor` traducidas.
 * `uploadConstraints()` no lo lleva, así que pasa sin tocar.
 */
export type BordeDeEscritura = {
  readonly [K in keyof DocumentsAdminApi]: Traducida<K>
}

/** Error del borde con la forma que el resto de la app ya usa (`HttpError`). */
function errorDelBorde(mensaje: string, statusCode: number): Error & { statusCode: number } {
  return Object.assign(new Error(mensaje), { statusCode })
}

/**
 * El acuerdo de cable de la escritura, todo junto y en un solo lugar.
 *
 * **Es una invención de esta pantalla, igual que la de lectura, y es el mismo
 * hallazgo abierto que `AW1` reportó en su §4-⓵:** `contracts/admin-api` es un
 * puerto de TypeScript —lo dice explícito en su cabecera— y no fija rutas, ni
 * verbos, ni códigos, ni cómo se serializan los archivos. «Lo que este contrato
 * NO fija, y es de `apps/admin-web`». Nada ata estas cuatro rutas a lo que
 * `apps/admin-web` vaya a servir.
 *
 * Vive en un archivo y en ningún otro para que, cuando ese acuerdo se cierre,
 * haya UN lugar que corregir.
 */
const RUTA_DOCUMENTOS = "documentos"
const RUTA_RESTRICCIONES = "documentos/restricciones"

/**
 * Los nombres de los campos del `multipart/form-data`.
 *
 * Constantes con nombre y no literales sueltos: son la mitad del acuerdo que
 * `admin-web` tiene que respetar, y quien lo implemente del otro lado va a
 * buscar exactamente esto.
 */
const CAMPO_ARCHIVOS = "archivos"
const CAMPO_TEMA = "tema"

/**
 * Una extensión tal como el contrato la exige: **con el punto**.
 *
 * `contracts/admin-api/index.ts:204-213` tipa `acceptedExtensions` como
 * ``readonly `.${string}`[]`` y explica por qué **esta traducción concreta** es
 * el punto de pérdida: «este valor cruza a un navegador que lo va a usar para
 * armar el `accept` de un `input[type=file]`, y la convención se pierde justo en
 * esa traducción».
 *
 * Un predicado de tipo y no un `if` suelto: es lo que hace que el valor entre al
 * resto de la aplicación ya tipado, sin ningún `as`.
 */
function esExtensionConPunto(valor: unknown): valor is `.${string}` {
  return typeof valor === "string" && valor.startsWith(".")
}

type LecturaDeRestricciones =
  | { readonly ok: true; readonly valor: UploadConstraints }
  | { readonly ok: false; readonly problema: string }

/**
 * Comprueba en RUNTIME lo que el tipo promete y `response.json()` no puede saber.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * QUÉ COMPRUEBA, Y QUÉ NO — porque la diferencia importa
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * **SÍ:** que el cuerpo tenga la FORMA que `UploadConstraints` declara — un
 * `maxBytes` numérico y finito, y una lista de extensiones que son strings y
 * empiezan con punto. Esto no es desconfianza: es que `await respuesta.json()`
 * devuelve `unknown` y el `as UploadConstraints` que había antes acá **borraba la
 * única garantía que el contrato se tomó el trabajo de poner en el tipo**. Un
 * cast no comprueba nada en runtime, y el borde es HTTP.
 *
 * **NO:** que los valores sean los CORRECTOS. Si la instancia acepta `.pdf` o
 * `.doc`, y si el máximo son 20 MB o 40, es `[CONFIG]` y la pantalla no tiene
 * cómo ni por qué opinar. Eso sigue siendo del borde — `guard` ② intacto.
 *
 * **Y NO completa nada.** No hay ningún `?? [".doc", ".docx", ".pdf"]`: si el
 * cuerpo no sirve, se lanza y el modal entra en su camino de «sin reglas
 * publicadas», donde no rechaza nada y la autoridad queda entera del lado del
 * borde (`guard` ①). Un default acá sería la constante prohibida disfrazada de
 * robustez.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * POR QUÉ NO SE NORMALIZA A MINÚSCULA, QUE ES LA TENTACIÓN OBVIA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * El contrato dice «CON el punto **y en minúscula**», y sería de una línea hacer
 * `.toLowerCase()` acá para que un `".PDF"` mal emitido igual funcione. **Sería
 * un error, y del tipo que el `guard` ② nombra.** El módulo compara la extensión
 * del archivo en minúscula contra esta lista **tal como está**
 * (`contracts/admin-api/fake.ts:161-164`). Si la pantalla normalizara y el módulo
 * no, con un `".PDF"` en la configuración la pantalla ACEPTARÍA archivos que el
 * módulo RECHAZA: la pantalla rechazando distinto que el banco, que es
 * exactamente el criterio incumplido «que nadie ve».
 *
 * Sin normalizar, las dos capas se comportan igual —las dos rechazan todo— y el
 * error de configuración se ve idéntico de los dos lados. Es lo correcto aunque
 * se lea como lo estricto.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * SE RECONSTRUYE EL VALOR CON DOS CAMPOS, NO SE DEVUELVE EL CUERPO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `UploadConstraints` es, en palabras del contrato, «una proyección de
 * `KnowledgeConfig`, no el config entero», y un `KnowledgeConfig` lo satisface
 * **estructuralmente**: un `admin-web` que devuelva el config entero pasa
 * cualquier chequeo y mete `chunkSize`, `chunkOverlap` y el modelo de embeddings
 * en una SPA estática que cualquiera puede abrir. Devolver un objeto nuevo con
 * los dos campos que este borde declara corta esa fuga sin costo. **No convierte
 * a la pantalla en juez de otra unidad** —no se reporta ni se falla por los
 * campos de más—: simplemente no se propagan.
 */
function leerRestricciones(valor: unknown): LecturaDeRestricciones {
  if (typeof valor !== "object" || valor === null || Array.isArray(valor)) {
    return { ok: false, problema: "el cuerpo no es un objeto JSON" }
  }

  const { maxBytes, acceptedExtensions } = valor as Record<string, unknown>

  /**
   * `Number.isFinite` y no sólo `typeof === "number"`.
   *
   * `NaN` es un `number` y es el único valor de este campo con un modo de falla
   * SILENCIOSO: `archivo.size > NaN` es siempre `false`, así que el control de
   * tamaño se apagaría sin que nada lo diga y el operador subiría 40 MB para que
   * el borde los rechace. Un `0` o un negativo también son un error, pero se ven
   * solos —rechazan todo— y el módulo se comporta igual.
   */
  if (typeof maxBytes !== "number" || !Number.isFinite(maxBytes)) {
    return {
      ok: false,
      problema: `\`maxBytes\` tiene que ser un número finito; llegó ${JSON.stringify(maxBytes)}`,
    }
  }

  if (!Array.isArray(acceptedExtensions)) {
    return {
      ok: false,
      problema: `\`acceptedExtensions\` tiene que ser un arreglo; llegó ${JSON.stringify(acceptedExtensions)}`,
    }
  }

  const extensiones: `.${string}`[] = []
  for (const entrada of acceptedExtensions) {
    if (!esExtensionConPunto(entrada)) {
      /**
       * El mensaje nombra el error EXACTO y muestra el valor.
       *
       * Es el punto entero de este chequeo: el síntoma de una lista sin punto es
       * catastrófico —cero archivos cargables, todos «formato no soportado»— y
       * su causa es invisible. Es el mismo bug que ya costó una corrida entera
       * con modelo real, sólo que llegando por el cable en vez de por un
       * `split(".")`. Este texto llega hasta el modal, así que quien opere la
       * instancia lee qué arreglar en `admin-web` en vez de «no anda».
       */
      return {
        ok: false,
        problema: `\`acceptedExtensions\` tiene que traer las extensiones CON el punto (".pdf", no "pdf"); llegó ${JSON.stringify(entrada)}`,
      }
    }
    extensiones.push(entrada)
  }

  return { ok: true, valor: { maxBytes, acceptedExtensions: extensiones } }
}

function esArregloDeResultados(valor: unknown): valor is readonly IngestOutcome[] {
  return (
    Array.isArray(valor) &&
    valor.every(
      (item) =>
        typeof item === "object" &&
        item !== null &&
        typeof (item as { name?: unknown }).name === "string" &&
        typeof (item as { accepted?: unknown }).accepted === "boolean",
    )
  )
}

/**
 * Arma el borde de escritura contra la `admin-web` que la instancia configuró.
 *
 * Función y no módulo con estado, por lo mismo que `crearProveedorDatos`: la
 * dirección llega en tiempo de ejecución (`configuracion.json`) y no hay ningún
 * momento de import en el que se la pueda conocer.
 */
export function crearBordeDeEscrituraHttp(config: ConfiguracionInstancia): BordeDeEscritura {
  const raiz = config.adminWebUrl

  /**
   * `credentials: "include"` en las cuatro, y ningún `Authorization` armado acá.
   *
   * Igual que en la lectura: un token que la pantalla tuviera que leer para
   * ponerlo en un header es un token que la pantalla tuvo que GUARDAR, y esta
   * unidad **no guarda nada, ni credenciales ni estado** (`arquitectura.html`
   * §1). Con cookie `HttpOnly` el JavaScript de la pantalla nunca la ve — y es
   * también lo que hace que el `actor` de la auditoría lo resuelva `admin-web`
   * de la sesión y no un campo que mande el cliente.
   */
  const pedir = async (ruta: string, init: RequestInit): Promise<Response> => {
    let respuesta: Response
    try {
      respuesta = await fetch(`${raiz}/${ruta}`, { ...init, credentials: "include" })
    } catch (causa) {
      throw Object.assign(errorDelBorde(`No se pudo hablar con el servidor (${raiz}).`, 0), {
        cause: causa,
      })
    }
    if (!respuesta.ok) {
      throw errorDelBorde(
        await mensajeDeError(respuesta, `El servidor respondió ${respuesta.status}.`),
        respuesta.status,
      )
    }
    return respuesta
  }

  return {
    async uploadConstraints(): Promise<UploadConstraints> {
      const respuesta = await pedir(RUTA_RESTRICCIONES, {
        method: "GET",
        headers: { Accept: "application/json" },
      })
      const lectura = leerRestricciones(await respuesta.json())
      if (!lectura.ok) {
        throw errorDelBorde(
          `El servidor contestó las restricciones de carga en una forma que no se entiende: ${lectura.problema}.`,
          502,
        )
      }
      return lectura.valor
    },

    /**
     * El lote, en UN request `multipart/form-data`.
     *
     * **Los archivos van en el orden de `mandato.files` y la respuesta se lee en
     * el mismo orden.** El contrato lo fija: «Devuelve un resultado POR ARCHIVO,
     * en el mismo orden de entrada […] emparejar resultados por nombre es ambiguo
     * justo en el caso que hay que mostrar bien» —dos archivos del mismo lote que
     * se llaman igual—. `FormData` conserva el orden de inserción de los valores
     * repetidos de una misma clave, así que la posición sobrevive el viaje.
     *
     * Un solo request y no uno por archivo: `upload` es una operación de LOTE en
     * el contrato, y partirla acá arriba haría que el criterio negativo de §P0-1
     * lo cumpla el navegador en vez del módulo — o sea, dejaría de estar
     * verificado donde el examen lo mira.
     */
    async upload(mandato: MandatoDeCarga): Promise<readonly IngestOutcome[]> {
      const cuerpo = new FormData()
      for (const archivo of mandato.files) {
        cuerpo.append(CAMPO_ARCHIVOS, archivo, archivo.name)
      }
      /**
       * El tema va sólo si hay tema. `null` significa «la instancia no usa
       * categorías» (`F1.md` §Pantalla: «en KAI puede ir vacío») y mandarlo como
       * el string `"null"` lo convertiría en un tema llamado «null».
       */
      if (mandato.topic !== null) {
        cuerpo.append(CAMPO_TEMA, mandato.topic)
      }

      const respuesta = await pedir(RUTA_DOCUMENTOS, { method: "POST", body: cuerpo })
      const resultados: unknown = await respuesta.json()

      if (!esArregloDeResultados(resultados)) {
        throw errorDelBorde("El servidor contestó la carga en una forma que no se entiende.", 502)
      }

      /**
       * Se exige un resultado por archivo, y se falla ruidosamente si no.
       *
       * El emparejamiento es POR POSICIÓN, así que una respuesta más corta o más
       * larga no produce un error: produce una pantalla que le atribuye a un
       * archivo el resultado de otro. Es el modo de falla más caro posible acá
       * —el operador leyendo «rechazado: ya existe» debajo del archivo
       * equivocado— y es invisible salvo que se chequee.
       */
      if (resultados.length !== mandato.files.length) {
        throw errorDelBorde(
          `El servidor contestó ${resultados.length} resultados para ${mandato.files.length} archivos. El contrato exige uno por archivo, en orden.`,
          502,
        )
      }
      return resultados
    },

    /**
     * Re-dispara el pipeline. `F1.md` §P0-2.
     *
     * **No se limpia nada antes.** `guard` ③ de `AW3`: la limpieza de fragmentos
     * parciales es de `K5` y vive atada al re-disparo, del otro lado. Un segundo
     * llamado desde acá correría sin transacción y sin ver el estado real del
     * pipeline. Acá hay UNA llamada y ninguna más.
     */
    async retry(mandato: MandatoDeDocumento): Promise<void> {
      await pedir(`${RUTA_DOCUMENTOS}/${encodeURIComponent(mandato.docId)}/reintentos`, {
        method: "POST",
      })
    },

    /** Borrado por `docId`. `F1.md` §P0-3. La cascada es del otro lado. */
    async remove(mandato: MandatoDeDocumento): Promise<void> {
      await pedir(`${RUTA_DOCUMENTOS}/${encodeURIComponent(mandato.docId)}`, { method: "DELETE" })
    },
  }
}
